import { useEffect, useMemo, useState } from 'react';

import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import { useMutation } from '@zenbusiness/application-commons-graphql';
import Box from '@zenbusiness/zui/Box';
import DialogContent from '@zenbusiness/zui/DialogContent';
import FormControl from '@zenbusiness/zui/FormControl';
import Heading from '@zenbusiness/zui/Heading';
import LoadingButton from '@zenbusiness/zui/LoadingButton';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';
import TextField from '@zenbusiness/zui/TextField';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { v1 as uuidv1 } from 'uuid';

import AutoCompleteInput from '#client/components/AutoCompleteInput';
import { FocusGrid, ContainerGrid, ActionButtonsGrid } from '#client/components/Grid';
import {
  OPERATING_ADDRESS_INVALID_MESSAGE,
  OPERATING_ADDRESS_IS_PO_BOX_MESSAGE,
  OPERATING_ADDRESS_IS_RA_ADDRESS_MESSAGE,
  ZIP_CODE_INVALID_MESSAGE
} from '#client/components/OpenAccountApplication/components/CustomFields/constants/errorMessages';
import { STATES_LIST } from '#client/components/OpenAccountApplication/components/CustomFields/constants/states';
import {
  regexZipCode,
  regexPOBox
} from '#client/components/OpenAccountApplication/components/CustomFields/utils/regex';
import { DB_APPLICATION_STATUS } from '#client/constants/application';
import { MODAL_NAMES } from '#client/constants/modals';
import useApplicationStatusContext from '#client/hooks/useApplicationStatusContext';
import useCheckOpenModal from '#client/hooks/useCheckOpenModals';
import useEnterSubmit from '#client/hooks/useEnterSubmit';
import useToastContext from '#client/hooks/useToastContext';
import { PATCH_APPLICATION_INIT_DATA_MUTATION } from '#client/mutations';

import { WrapperContentStyled } from '../EnterOperatingAddressModal.styles';

const ALLOW_ENTER_OPERATING_ADDRESS_STATUS_LIST = [
  DB_APPLICATION_STATUS.AWAITING_OPERATING_ADDRESS
];

function EnterOperatingAddress({ handleClose }) {
  const [values, setValues] = useState({
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    zip: '',
    valid: true,
    isDirty: false,
    isFocus: false
  });
  const [selectedState, setSelectedState] = useState(null);
  const [isPOBox, setIsPOBox] = useState(false);
  const [isValidZipCode, setIsValidZipCode] = useState(true);
  const [raErrorMessage, setRaErrorMessage] = useState('');

  // Clear RA error when user starts editing
  const clearRaError = () => {
    if (raErrorMessage) {
      setRaErrorMessage('');
    }
  };

  const [patchApplicationData, { loading: patchApplicationLoading }] = useMutation(
    PATCH_APPLICATION_INIT_DATA_MUTATION,
    {
      noThrow: true
    }
  );
  const { createCommonErrorToast, createSuccessToast, clearAllToast } = useToastContext();
  const { isOpeningModal } = useCheckOpenModal();
  const navigate = useNavigate();
  const submitButtonId = useEnterSubmit();
  const {
    appStatus,
    applicationId,
    loading: applicationStatusLoading,
    appStatusError
  } = useApplicationStatusContext();
  const { error: businessEntityError } = useBusinessEntity();

  useEffect(() => {
    if (businessEntityError) {
      createCommonErrorToast({ error: businessEntityError });
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessEntityError]);

  const isShowError = values.isDirty && !values.valid && !values.isFocus;

  // Helper function to check if the RA error is about registered agent address
  const isRAAddressError = raErrorMessage.toLowerCase().includes('registered agent address');

  const shouldDisableSubmitBtn = useMemo(
    () =>
      !values.addressLine1 ||
      !values.city ||
      !values.state ||
      !values.zip ||
      !values.valid ||
      isPOBox ||
      raErrorMessage.toLowerCase().includes('registered agent address') ||
      !isValidZipCode ||
      !!businessEntityError ||
      appStatusError ||
      applicationStatusLoading,
    [
      appStatusError,
      applicationStatusLoading,
      businessEntityError,
      values.addressLine1,
      values.city,
      values.state,
      values.zip,
      values.valid,
      isPOBox,
      raErrorMessage,
      isValidZipCode
    ]
  );

  const refetchAppStatusHandler = () => {
    // TODO: will move clearAllToast to APPLICATION_RECEIVED_MODAL
    clearAllToast();
    navigate(`?modal=${MODAL_NAMES.APPLICATION_RECEIVED_MODAL}`, { replace: true });
    /**
     * isOpeningModal is true when a user open a modal via URL
     * or open from Zendash dashboard e.g`?modal=enter-operating-address`.
     * in this case if we call `handleClose` it will redirect to `/dashboard` instead
     */
    if (!isOpeningModal) {
      handleClose();
    }
  };

  const handleSubmit = async () => {
    const { data: patchData, error } = await patchApplicationData({
      variables: {
        input: {
          bankingApplicationId: applicationId,
          operationalAddressLine1: values.addressLine1,
          operationalAddressLine2: values.addressLine2,
          operationalCity: values.city,
          operationalState: values.state,
          operationalCountry: 'US',
          operationalPostalCode: values.zip
        }
      }
    });

    if (error || patchData?.status === 'Fail') {
      // Check if it's a 422 error
      if (error?.networkError?.statusCode === 422 || error?.statusCode === 422) {
        const errorMessage =
          error?.message ||
          error?.networkError?.result?.message ||
          'Invalid address data provided.';
        setRaErrorMessage(errorMessage);
      } else {
        createCommonErrorToast({ error });
      }
    } else {
      // Clear any previous RA error message on success
      setRaErrorMessage('');
      createSuccessToast({
        title: 'Your address has been submitted'
      });
      refetchAppStatusHandler();
    }
  };

  const isNotAllowEnterOperatingAddress =
    appStatus && !ALLOW_ENTER_OPERATING_ADDRESS_STATUS_LIST.includes(appStatus);
  const isAllowEnterOperatingAddress =
    appStatus && ALLOW_ENTER_OPERATING_ADDRESS_STATUS_LIST.includes(appStatus);

  useEffect(() => {
    /**
     * If application status is not null and not equal to awaiting operating address
     * then close the modal
     */
    if (isNotAllowEnterOperatingAddress) {
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNotAllowEnterOperatingAddress]);

  if (isAllowEnterOperatingAddress) {
    return (
      <>
        <DialogContent>
          <ContainerGrid>
            <FocusGrid>
              <WrapperContentStyled>
                <Stack
                  alignItems="flex-start"
                  mb="40px"
                  spacing="16px"
                >
                  <Heading
                    size="3xl"
                    weight="bold"
                  >
                    To finish your application, confirm your operating address.
                  </Heading>
                  <Text
                    color="tertiary"
                    size="lg"
                  >
                    This is the physical address where your business conducts its main operations.
                  </Text>
                  {raErrorMessage && !isRAAddressError && (
                    <Text
                      color="red"
                      size="md"
                    >
                      {raErrorMessage}
                    </Text>
                  )}
                </Stack>
                <Box>
                  <Stack
                    spacing="16px"
                    sx={{ paddingTop: '24px' }}
                  >
                    {/* Address Line 1 and Address Line 2 Row */}
                    <Stack
                      direction="row"
                      spacing="16px"
                    >
                      <Box width="70%">
                        <FormControl
                          fullWidth
                          variant="outlined"
                        >
                          <TextField
                            error={isShowError || isPOBox || isRAAddressError}
                            id="addressLine1"
                            inputProps={{ maxLength: 200 }}
                            label="Address"
                            onBlur={() => setValues({ ...values, isDirty: true, isFocus: false })}
                            onChange={(e) => {
                              clearRaError();
                              const { value } = e.target;
                              const isPOBoxAddress = regexPOBox.test(value);
                              setIsPOBox(isPOBoxAddress);
                              setValues({ ...values, addressLine1: value, valid: !isPOBoxAddress });
                            }}
                            onFocus={() => setValues({ ...values, isFocus: true })}
                            type="text"
                            value={values.addressLine1}
                          />
                          {isPOBox && (
                            <Text
                              color="red"
                              component="div"
                              pt="8px"
                              size="xs"
                            >
                              {OPERATING_ADDRESS_IS_PO_BOX_MESSAGE}
                            </Text>
                          )}
                          {isRAAddressError && (
                            <Text
                              color="red"
                              component="div"
                              pt="8px"
                              size="xs"
                            >
                              {OPERATING_ADDRESS_IS_RA_ADDRESS_MESSAGE}
                            </Text>
                          )}
                        </FormControl>
                      </Box>
                      <Box width="30%">
                        <FormControl
                          fullWidth
                          variant="outlined"
                        >
                          <TextField
                            id="addressLine2"
                            inputProps={{ maxLength: 20 }}
                            label="Unit/Apt/Suite"
                            onBlur={() => setValues({ ...values, isDirty: true, isFocus: false })}
                            onChange={(e) =>
                              setValues({
                                ...values,
                                addressLine2: e.target.value,
                                valid: !isPOBox
                              })
                            }
                            onFocus={() => setValues({ ...values, isFocus: true })}
                            type="text"
                            value={values.addressLine2}
                          />
                        </FormControl>
                      </Box>
                    </Stack>

                    {/* City Row */}
                    <FormControl
                      fullWidth
                      variant="outlined"
                    >
                      <TextField
                        error={isShowError}
                        id="city"
                        inputProps={{ maxLength: 200 }}
                        label="City"
                        onBlur={() => setValues({ ...values, isDirty: true, isFocus: false })}
                        onChange={(e) =>
                          setValues({ ...values, city: e.target.value, valid: !isPOBox })
                        }
                        onFocus={() => setValues({ ...values, isFocus: true })}
                        type="text"
                        value={values.city}
                      />
                    </FormControl>

                    {/* State and ZIP Row */}
                    <Stack
                      direction="row"
                      spacing="16px"
                    >
                      <Box width="50%">
                        <FormControl
                          fullWidth
                          variant="outlined"
                        >
                          <AutoCompleteInput
                            blurOnSelect
                            getOptionLabel={(option) => option.value || ''}
                            onChange={(event, newValue) => {
                              setSelectedState(newValue);
                              const stateValue = newValue?.key || '';
                              setValues((prev) => ({
                                ...prev,
                                state: stateValue,
                                valid: !isPOBox
                              }));
                            }}
                            openOnFocus
                            options={STATES_LIST}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                inputProps={{
                                  ...params.inputProps,
                                  name: uuidv1()
                                }}
                                label="State"
                                onBlur={() =>
                                  setValues((prev) => ({ ...prev, isDirty: true, isFocus: false }))
                                }
                                onFocus={() => setValues((prev) => ({ ...prev, isFocus: true }))}
                              />
                            )}
                            value={selectedState}
                          />
                        </FormControl>
                      </Box>
                      <Box width="50%">
                        <FormControl
                          fullWidth
                          variant="outlined"
                        >
                          <TextField
                            error={isShowError || !isValidZipCode}
                            id="zip"
                            inputProps={{ maxLength: 10 }}
                            label="ZIP Code"
                            onBlur={() => {
                              const validZipCode = regexZipCode.test(values.zip);
                              setIsValidZipCode(validZipCode);
                              setValues({ ...values, isDirty: true, isFocus: false });
                            }}
                            onChange={(e) => {
                              const inputValue = e.target.value;
                              const numbersOnly = inputValue.replace(/\D/g, '');
                              let formattedValue = '';
                              formattedValue += numbersOnly.slice(0, 5);
                              if (numbersOnly.length > 5) {
                                formattedValue += `-${numbersOnly.slice(5, 9)}`;
                              }
                              const validZipCode = regexZipCode.test(formattedValue);
                              setIsValidZipCode(validZipCode);
                              setValues({ ...values, zip: formattedValue, valid: !isPOBox });
                            }}
                            onFocus={() => setValues({ ...values, isFocus: true })}
                            type="text"
                            value={values.zip}
                          />
                          {!isValidZipCode && values.isDirty && (
                            <Text
                              color="red"
                              component="div"
                              pt="8px"
                              size="xs"
                            >
                              {ZIP_CODE_INVALID_MESSAGE}
                            </Text>
                          )}
                        </FormControl>
                      </Box>
                    </Stack>
                  </Stack>
                  {isShowError && (
                    <Text
                      color="red"
                      pt="8px"
                      size="xs"
                    >
                      {OPERATING_ADDRESS_INVALID_MESSAGE}
                    </Text>
                  )}
                </Box>
              </WrapperContentStyled>
            </FocusGrid>
          </ContainerGrid>
        </DialogContent>
        <ActionButtonsGrid>
          <LoadingButton
            color="primary"
            disabled={shouldDisableSubmitBtn}
            fullWidth
            id={submitButtonId}
            loading={patchApplicationLoading || applicationStatusLoading}
            onClick={handleSubmit}
            variant="contained"
          >
            Submit
          </LoadingButton>
        </ActionButtonsGrid>
      </>
    );
  }

  return null;
}

EnterOperatingAddress.propTypes = {
  handleClose: PropTypes.func.isRequired
};

export default EnterOperatingAddress;
